import { datadogLogs } from "@datadog/browser-logs";
import type { TeamWith } from "@redotech/account-common/base-team";
import { PrintJobTrigger } from "@redotech/congo-enums/print-job-trigger";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { getKey } from "@redotech/react-util/key";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { useStore } from "@redotech/react-util/store";
import {
  PrintableDocumentsDisplayName,
  PrintKind,
  PrintKindForDocument,
} from "@redotech/redo-fulfillment-group-common/fulfillment-print-status";
import {
  type BulkOneClickPrintPickListPosition,
  PrintableDocument,
} from "@redotech/redo-fulfillment-group-common/print-job";
import { PageSize } from "@redotech/redo-fulfillment-operations-common/packing-slips/packing-slip-builder";
import { PackingSlip } from "@redotech/redo-fulfillment-operations-common/packing-slips/packing-slip-definition";
import { PersonalizedSlip } from "@redotech/redo-fulfillment-operations-common/personalized-slip/personalized-slip-definition";
import {
  PageFormat,
  PersonalizedSlipAutoPrintTrigger,
} from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import {
  type PrintProfile,
  type PrintSettings,
  resolveDefaultPrintSettings,
} from "@redotech/redo-fulfillment-outbound-labels-common/print-settings";
import {
  getPrintSettings,
  type Workstation,
} from "@redotech/redo-fulfillment-printing-common/workstation";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import InfoCircleIcon from "@redotech/redo-icons/info-circle.svg";
import Monitor from "@redotech/redo-icons/monitor-01.svg";
import Printer from "@redotech/redo-icons/printer.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import type {
  ConsolidationCartonId,
  ConsolidationId,
} from "@redotech/redo-warehouse-id";
import { toast } from "@redotech/redo-web/alert";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalHeader } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoHorizontalTabs } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import CircleSpinner from "@redotech/redo-web/circle-spinner.svg";
import { Flex } from "@redotech/redo-web/flex";
import { Action0PortalContext } from "@redotech/redo-web/page";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { ClientError } from "@redotech/rpc/errors";
import { bijectionOptional } from "@redotech/util/bijection";
import { identity } from "@redotech/util/function";
import { deepCopyObject } from "@redotech/util/object";
import { sleep } from "@redotech/util/schedule";
import { storeTransform } from "@redotech/util/store";
import { numberStringFormat } from "@redotech/util/string-format";
import { assertNever } from "@redotech/util/type";
import { localStore } from "@redotech/web-util/storage";
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from "notistack";
import {
  type ContextType,
  createContext,
  memo,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import type {
  CartonLabelPrintResult,
  PrintCartonLabelInput,
} from "../scan/packing-station/use-carton-label-printer";
import {
  defaultPrintProfile,
  WorkstationSettingsModal,
} from "../setting/print-setup";
import { LiveOmsSettingsContext } from "./live-oms-settings-context";
import { getShouldPrintPackingSlipOnLabelPrinted } from "./packing-slip-on-label-printed";
import { getPersonalizedSlipPrinterForLabelPrint } from "./personalized-slip-printer";
import {
  PinVerificationType,
  usePinVerification,
} from "./pin-verification-context";
import {
  doPrintBatch,
  getBulkOneClickPrintPickListInput,
  type PrintableBatch,
  printConsolidationCartonLabelIfConfigured,
  printConsolidationCartonShippingLabelIfConfigured,
  type PrintOptions,
  printParcelBoxLabelIfConfigured,
} from "./print-batch";
import * as styles from "./print-batch-modal.module.css";
import {
  type DocumentPrintStatusMap,
  getDocumentTabs,
} from "./print-batch-tabs";
import { PrintQueueContext } from "./print-queue-context";
import { PrinterContext, redoConnectComputerIdStore } from "./printer-context";
import { RedoConnectWarning } from "./redo-connect-warning";
import { ReprintConfirmationModal } from "./reprint-confirmation-modal";
import {
  weatherMissingAddressDescription,
  weatherMissingAddressTitle,
} from "./util";

/** The per-group fields the document tab/print-status map is derived from. */
interface FulfillmentGroupDocumentState {
  printStatus?: Partial<Record<PrintKind, boolean | null | undefined>> | null;
  personalizedSlipGeneratedUrl?: string | null;
}

const PRINTABLE_DOCUMENTS_WITH_PRINT_STATUS: readonly PrintableDocument[] = [
  PrintableDocument.LABEL,
  PrintableDocument.PACKING_SLIP,
  PrintableDocument.PICK_LIST,
  PrintableDocument.COMMERCIAL_INVOICE,
  PrintableDocument.PERSONALIZED_SLIP,
  PrintableDocument.RETURN_LABEL,
  PrintableDocument.LOCAL_PICK_UP_LABEL,
  PrintableDocument.BILL_OF_LADING,
  PrintableDocument.PARCEL_PACKING_SLIP,
  PrintableDocument.ORDER_SUMMARY,
];

export function createDocumentPrintStatusMap(
  needsPrintingByDocument: Partial<Record<PrintableDocument, boolean>> = {},
): DocumentPrintStatusMap {
  const documentPrintStatusMap: DocumentPrintStatusMap = {
    [PrintableDocument.PACKING_SLIP]: {
      needsPrinting:
        needsPrintingByDocument[PrintableDocument.PACKING_SLIP] ?? true,
    },
    [PrintableDocument.PICK_LIST]: {
      needsPrinting:
        needsPrintingByDocument[PrintableDocument.PICK_LIST] ?? true,
    },
    [PrintableDocument.ORDER_SUMMARY]: {
      needsPrinting:
        needsPrintingByDocument[PrintableDocument.ORDER_SUMMARY] ?? true,
    },
  };

  for (const document of PRINTABLE_DOCUMENTS_WITH_PRINT_STATUS) {
    const needsPrinting = needsPrintingByDocument[document];
    if (needsPrinting !== undefined) {
      documentPrintStatusMap[document] = { needsPrinting };
    }
  }

  return documentPrintStatusMap;
}

export function getDocumentPrintStatusMapForFulfillmentGroups(
  fulfillmentGroups: readonly FulfillmentGroupDocumentState[],
): DocumentPrintStatusMap {
  const needsPrintingByDocument: Partial<Record<PrintableDocument, boolean>> =
    {};
  for (const document of PRINTABLE_DOCUMENTS_WITH_PRINT_STATUS) {
    const printKind = PrintKindForDocument[document];

    needsPrintingByDocument[document] = fulfillmentGroups.some(
      (g) => !g.printStatus?.[printKind],
    );
  }

  const map = createDocumentPrintStatusMap(needsPrintingByDocument);
  const personalizedSlipStatus = map[PrintableDocument.PERSONALIZED_SLIP];
  if (personalizedSlipStatus) {
    personalizedSlipStatus.hasGeneratedInsert = fulfillmentGroups.some(
      (g) => !!g.personalizedSlipGeneratedUrl,
    );
  }
  return map;
}

class BatchFailedError extends Error {
  shipmentErrorMessages: string[];

  constructor(message: string, shipmentErrorMessages: string[] = []) {
    super(message);
    this.name = "BatchFailedError";
    this.shipmentErrorMessages = shipmentErrorMessages;
  }
}

const DOCUMENT_PLURAL_LABELS: Record<PrintableDocument, string> = {
  [PrintableDocument.LABEL]: "labels",
  [PrintableDocument.PACKING_SLIP]: "packing slips",
  [PrintableDocument.PICK_LIST]: "pick lists",
  [PrintableDocument.COMMERCIAL_INVOICE]: "commercial invoices",
  [PrintableDocument.PERSONALIZED_SLIP]: "personalized inserts",
  [PrintableDocument.RETURN_LABEL]: "return labels",
  [PrintableDocument.LOCAL_PICK_UP_LABEL]: "local pick up labels",
  [PrintableDocument.BILL_OF_LADING]: "bills of lading",
  [PrintableDocument.PARCEL_PACKING_SLIP]: "package packing slips",
  [PrintableDocument.ORDER_SUMMARY]: "order summaries",
};

function getNeedsPrintingForDocument(
  documentPrintStatusMap: DocumentPrintStatusMap,
  document: PrintableDocument,
) {
  return documentPrintStatusMap[document]?.needsPrinting ?? true;
}

function getDocumentsMarkedByPrint(
  document: PrintableDocument,
  bulkOneClickPrintPickListPosition?: BulkOneClickPrintPickListPosition,
) {
  const documents = [document];
  if (
    document === PrintableDocument.LABEL &&
    bulkOneClickPrintPickListPosition
  ) {
    documents.push(PrintableDocument.PICK_LIST);
  }
  return documents;
}

export interface PrintBatchInput {
  document: PrintableDocument;
  batch: PrintableBatch;
  documentPrintStatusMap: DocumentPrintStatusMap;
  bulkOneClickPrintPickListPosition?: BulkOneClickPrintPickListPosition;
  mixedPackingSlipSizes?: boolean;
  skipPrinter?: boolean;
  somePrinted?: boolean;
  packingSlipIds?: string[];
  personalizedSlipIds?: string[];
  /** What caused this print; defaults to MANUAL when a caller doesn't say. */
  trigger?: PrintJobTrigger;
}

interface ParcelBoxLabelPrintInput {
  fulfillmentGroupId: string;
  parcelIndex: number;
}

interface ConsolidationCartonShippingLabelPrintInput {
  cartonId: ConsolidationCartonId;
  consolidationId: ConsolidationId;
}

export const PrintBatchContext = createContext<{
  printBatch: (input: PrintBatchInput) => void;
  tryPrintConsolidationCartonLabel: (
    input: PrintCartonLabelInput,
  ) => Promise<CartonLabelPrintResult>;
  tryPrintConsolidationCartonShippingLabel: (
    input: ConsolidationCartonShippingLabelPrintInput,
  ) => Promise<CartonLabelPrintResult>;
  tryPrintParcelBoxLabel: (input: ParcelBoxLabelPrintInput) => Promise<boolean>;
  disabled?: boolean;
}>({
  printBatch: () => {},
  tryPrintConsolidationCartonLabel: async () => "preview-required",
  tryPrintConsolidationCartonShippingLabel: async () => "preview-required",
  tryPrintParcelBoxLabel: async () => false,
  disabled: false,
});

export const PackingSlipTemplatesContext = createContext<PackingSlip[]>([]);
export const PersonalizedSlipTemplatesContext = createContext<
  PersonalizedSlip[]
>([]);
export const WorkstationContext = createContext<Workstation | undefined>(
  undefined,
);

export const PrintBatchProvider = memo(function PrintBatchProvider({
  children,
  onClose,
  onDocumentPrinted,
}: {
  children: ReactNode;
  onDocumentPrinted?: (
    batch: PrintableBatch,
    document: PrintableDocument,
  ) => void;
  onClose?: () => void;
}) {
  const team = useRequiredContext(TeamContext);
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const printQueue = useContext(PrintQueueContext);
  const actionPortal = useContext(Action0PortalContext);
  const { executeWithPinCheck } = usePinVerification();
  const [showReprintConfirmation, setShowReprintConfirmation] = useState(false);
  const user = useContext(UserContext);
  const [modalData, setModalData] = useState<{
    batch: PrintableBatch;
    document: PrintableDocument;
    documentPrintStatusMap: DocumentPrintStatusMap;
    bulkOneClickPrintPickListPosition?: BulkOneClickPrintPickListPosition;
    templateId?: string;
    workstationId?: string;
  } | null>(null);

  const printInput = useRef<PrintBatchInput | null>(null);

  // Load packing slip templates
  const packingSlipTemplatesLoad = useLoad(
    async (signal) =>
      await merchantRpcClient.getPackingSlipTemplatesByTeam(
        { teamId: team._id },
        { signal },
      ),
    [team._id, merchantRpcClient],
  );

  // Load personalized insert templates
  const personalizedSlipTemplatesLoad = useLoad(
    async (signal) =>
      await client.getPersonalizedSlipTemplatesByTeam(
        { teamId: team._id },
        { signal },
      ),
    [team._id, client],
  );
  const printProfilesLoad = useLoad(
    async (signal) => await client.listPrintProfiles({}, { signal }),
    [client],
  );

  const { computers, printers, loading, printerIdIndex } =
    useContext(PrinterContext);
  const { isRedoConnectDisabled } = useContext(LiveOmsSettingsContext);

  const [selectedPrinter, setSelectedPrinter] = useStore(printerIdStore);
  const [redoConnectComputerId] = useStore(redoConnectComputerIdStore);
  const [workstationModalOpen, setWorkstationModalOpen] = useState(false);

  const printerOptions: TextRedoListItem<number>[] = useMemo(() => {
    const options: TextRedoListItem<number>[] =
      printers.map<TextRedoListItem<number>>((printer) => ({
        id: printer.id,
        value: printer.id,
        type: "text",
        text: printer.name.replaceAll("_", " "),
        leadingItem: {
          type: "status",
          color: printer.state === "online" ? "green" : "gray",
        },
      })) || [];
    if (options.length) {
      options.push({ value: 0, id: 0, type: "text", text: "Download PDF" });
    }
    return options;
  }, [printers]);

  const selectedPrinterOption = useMemo(() => {
    if (selectedPrinter === undefined) {
      return undefined;
    }
    const selectedOption = printerOptions.find(
      (option) => option.value === selectedPrinter,
    );
    if (!selectedOption) {
      return undefined;
    }
    const copy = deepCopyObject(selectedOption);
    copy.leadingItem = { type: "icon", Icon: Printer };
    return copy;
  }, [selectedPrinter, printerOptions]);

  const [selectedWorkstation, setSelectedWorkstation] =
    useStore(workstationIdStore);
  const workstations = team?.settings?.outboundLabels?.workstations;
  const printProfiles = useMemo(
    () => [
      defaultPrintProfile,
      ...(printProfilesLoad.value?.printProfiles ?? []),
    ],
    [printProfilesLoad.value?.printProfiles],
  );

  const selectedWorkstationObject = useMemo(
    () => workstations?.find((w) => w._id === selectedWorkstation),
    [workstations, selectedWorkstation],
  );

  const missingPrinterError =
    !loading &&
    selectedWorkstationObject?.selectedPrinters.some(
      ({ printerId }) => printerIdIndex[printerId] === undefined,
    );

  const printerError = selectedWorkstationObject?.selectedPrinters.some(
    ({ printerId }) => {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const printer = printers[printerIdIndex[printerId]];
      return printer && printer.state !== "online";
    },
  );

  const computerError = selectedWorkstationObject?.selectedPrinters.some(
    ({ printerId }) => {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const printer = printers[printerIdIndex[printerId]];
      return printer && printer.computer.state !== "connected";
    },
  );

  const workstationError = missingPrinterError || printerError || computerError;
  const redoConnectComputer = computers.find(
    (computer) => computer.id === redoConnectComputerId,
  );
  const redoConnectComputerError =
    !loading &&
    redoConnectComputerId !== undefined &&
    redoConnectComputer?.state !== "connected";

  const workstationOptions = useWorkstationOptions(workstations);

  const selectedWorkstationOption = useMemo(() => {
    const selectedOption =
      workstationOptions.find(
        (option) => option.value === selectedWorkstation,
      ) || workstationOptions.find((option) => option.value === "");

    if (!selectedOption) {
      return undefined;
    }

    const copy = deepCopyObject(selectedOption);
    copy.leadingItem = { type: "icon", Icon: Monitor };

    return copy;
  }, [selectedWorkstation, workstationOptions]);

  const workstation = workstations?.find(
    (option) => option._id && option._id === selectedWorkstationOption?.value,
  );

  const tryPrintParcelBoxLabel = useHandler(
    async (input: ParcelBoxLabelPrintInput) => {
      if (missingPrinterError) {
        toast("Printer no longer available. Reselect it before printing.", {
          variant: "error",
        });
        setWorkstationModalOpen(true);
        return true;
      }

      const printed = await printParcelBoxLabelIfConfigured({
        client,
        input,
        workstation,
        selectedPrinterId: selectedPrinterOption?.value,
        disabled: isRedoConnectDisabled,
      });
      if (printed) {
        toast("Box label sent to printer", { variant: "success" });
      }
      return printed;
    },
  );

  const tryPrintConsolidationCartonLabel = useHandler(
    async (input: PrintCartonLabelInput): Promise<CartonLabelPrintResult> => {
      if (missingPrinterError) {
        toast("Printer no longer available. Reselect it before printing.", {
          variant: "error",
        });
        setWorkstationModalOpen(true);
        return "printer-configuration-required";
      }

      const printed = await printConsolidationCartonLabelIfConfigured({
        client,
        disabled: isRedoConnectDisabled,
        input,
        selectedPrinterId: selectedPrinterOption?.value,
        workstation,
      });
      return printed ? "printed" : "preview-required";
    },
  );

  const tryPrintConsolidationCartonShippingLabel = useHandler(
    async (
      input: ConsolidationCartonShippingLabelPrintInput,
    ): Promise<CartonLabelPrintResult> => {
      if (missingPrinterError) {
        toast("Printer no longer available. Reselect it before printing.", {
          variant: "error",
        });
        setWorkstationModalOpen(true);
        return "printer-configuration-required";
      }

      const printed = await printConsolidationCartonShippingLabelIfConfigured({
        client,
        disabled: isRedoConnectDisabled,
        input,
        selectedPrinterId: selectedPrinterOption?.value,
        workstation,
      });
      if (printed) {
        toast("Shipping label sent to printer", { variant: "success" });
        return "printed";
      }
      return "preview-required";
    },
  );

  const requestPrint = useHandler((input: PrintBatchInput) => {
    if (missingPrinterError) {
      toast("Printer no longer available. Reselect it before printing.", {
        variant: "error",
      });
      setWorkstationModalOpen(true);
      return;
    }
    if (
      workstation?.printProfileId &&
      !printProfilesLoad.value &&
      !printProfilesLoad.error
    ) {
      toast("Print profiles are still loading. Try again in a moment.", {
        variant: "error",
      });
      return;
    }

    const printSettings = getPrintSettings(
      resolveDefaultPrintSettings(team.settings.outboundLabels.documents),
      printProfilesLoad.value?.printProfiles,
      team.settings.outboundLabels.workstations,
      workstation?._id,
    );
    if (
      input.document === PrintableDocument.LABEL &&
      input.mixedPackingSlipSizes &&
      printSettings.shippingLabel?.includePackingSlip
    ) {
      input.skipPrinter = true;
    }

    // Should only be true if Redo Connect/Print Node is DOWN and we force everybody to stop using it
    if (isRedoConnectDisabled) {
      datadogLogs.logger.info(
        "Redo Connect is disabled for everyone, skipping printer",
        {
          userId: user?._id,
          team: team.name,
          document: input.document,
          batch: input.batch,
        },
      );
      input.skipPrinter = true;
    }
    printInput.current = input;

    const needsPrinting = getNeedsPrintingForDocument(
      input.documentPrintStatusMap,
      input.document,
    );

    // Make them confirm if we either already printed some of/all of the labels or packing slips
    if (
      (!needsPrinting || input.somePrinted) &&
      (input.document === PrintableDocument.LABEL ||
        input.document === PrintableDocument.PACKING_SLIP)
    ) {
      const isPackingSlip = input.document === PrintableDocument.PACKING_SLIP;
      executeWithPinCheck(
        () => setShowReprintConfirmation(true),
        {
          type: isPackingSlip
            ? PinVerificationType.ReprintPackingSlip
            : PinVerificationType.ReprintLabel,
        },
        {
          title: isPackingSlip
            ? "Reprint Packing Slip PIN Verification"
            : "Reprint Label PIN Verification",
          description: isPackingSlip
            ? "Please enter your PIN to proceed with reprinting the packing slip."
            : "Please enter your PIN to proceed with reprinting the label.",
        },
      );
    } else {
      printBatch(input);
    }
  });

  const printBatch = useHandler((input: PrintBatchInput) => {
    let printOptions: PrintOptions = {};

    const {
      batch,
      document,
      documentPrintStatusMap,
      bulkOneClickPrintPickListPosition,
      skipPrinter = false,
    } = input;

    const teamDefaultPackingSlip = packingSlipTemplatesLoad.value?.find(
      (t) => t._id === team?.settings?.outboundLabels?.packingSlipId,
    );

    const packingSlips: PackingSlip[] = input.packingSlipIds?.length
      ? (input.packingSlipIds
          ?.map((id) =>
            packingSlipTemplatesLoad.value?.find((t) => t._id === id),
          )
          .filter((t): t is PackingSlip => t !== undefined) ?? [])
      : [];

    if (teamDefaultPackingSlip && !packingSlips.length) {
      packingSlips.push(teamDefaultPackingSlip);
    }

    const personalizedSlips: PersonalizedSlip[] = input.personalizedSlipIds
      ?.length
      ? (input.personalizedSlipIds
          ?.map((id) =>
            personalizedSlipTemplatesLoad.value?.find((t) => t._id === id),
          )
          .filter((t): t is PersonalizedSlip => t !== undefined) ?? [])
      : [];

    if (workstation) {
      printOptions = handleWorkstationPrinting({
        batch,
        document,
        packingSlips,
        personalizedSlips,
        printProfiles: printProfilesLoad.value?.printProfiles ?? [],
        printQueue,
        team,
        workstation,
      });
    } else if (selectedPrinterOption?.value) {
      printOptions = { printerId: selectedPrinterOption.value };
    }

    if (!skipPrinter && printOptions.printerId) {
      const documentTitle = getPluralAwareDocumentTitle(batch, document);
      const addToPrintQueue = async () => {
        if (!printQueue) {
          throw new Error("Print queue not found");
        }
        printQueue.addJob({
          documentType: document,
          ...getBulkOneClickPrintPickListInput(
            bulkOneClickPrintPickListPosition,
          ),
          ...batch,
          ...printOptions,
          trigger: input.trigger ?? PrintJobTrigger.Manual,
          workstationId: workstation?._id,
        });
        return true;
      };
      enqueueSnackbar(
        <Text fontSize="sm">Adding {documentTitle} to print queue</Text>,
        {
          variant: "default",
          persist: true,
          action: (id) => (
            <AddToPrintQueueToastAction
              addToPrintQueue={addToPrintQueue}
              snackbarId={id}
            />
          ),
        },
      );
    } else {
      setModalData({
        batch,
        document,
        documentPrintStatusMap,
        ...getBulkOneClickPrintPickListInput(bulkOneClickPrintPickListPosition),
        templateId: printOptions.templateId,
        workstationId: printOptions.workstationId,
      });
    }
  });

  const isBatch =
    printInput.current?.batch && "batchId" in printInput.current.batch;
  const isPlural =
    printInput.current?.batch &&
    "fulfillmentGroupIds" in printInput.current.batch &&
    printInput.current.batch.fulfillmentGroupIds.length > 1;
  const someNeedPrinting = printInput.current
    ? getNeedsPrintingForDocument(
        printInput.current.documentPrintStatusMap,
        printInput.current.document,
      )
    : false;

  let printerStatus = null;
  let dropdownClassname = "";
  if (redoConnectComputerError) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    dropdownClassname = styles.workstationSelectError;
    printerStatus = (
      <Tooltip
        leftAlign
        title="Make sure Redo Connect is running on this computer. After opening it, the connection status may take a moment to update."
      >
        <Flex align="center" gap="xs" justify="center">
          <Text fontSize="xs" textColor="error">
            Redo Connect disconnected
          </Text>
          <RedoIcon color="tertiary" Icon={InfoCircleIcon} size={16} />
        </Flex>
      </Tooltip>
    );
  } else if (workstationError) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    dropdownClassname = styles.workstationSelectError;
    printerStatus = (
      <Flex align="center" justify="center">
        <Text
          cursor="pointer"
          fontSize="xs"
          hoverTextColor="primary"
          onClick={() => setWorkstationModalOpen(true)}
          textColor="error"
          textDecoration="underline"
        >
          {missingPrinterError
            ? "Printer no longer available"
            : printerError
              ? "Printer connection issue"
              : "Computer connection issue"}
        </Text>
      </Flex>
    );
  }

  return (
    <>
      <PackingSlipTemplatesContext.Provider
        value={packingSlipTemplatesLoad.value || []}
      >
        <PersonalizedSlipTemplatesContext.Provider
          value={personalizedSlipTemplatesLoad.value || []}
        >
          <PrintBatchContext.Provider
            value={{
              printBatch: requestPrint,
              tryPrintConsolidationCartonLabel,
              tryPrintConsolidationCartonShippingLabel,
              tryPrintParcelBoxLabel,
              disabled: printQueue?.enqueuePending || missingPrinterError,
            }}
          >
            <WorkstationContext.Provider value={workstation}>
              {children}
            </WorkstationContext.Provider>
          </PrintBatchContext.Provider>
        </PersonalizedSlipTemplatesContext.Provider>
      </PackingSlipTemplatesContext.Provider>
      <ReprintConfirmationModal
        disabled={printQueue?.enqueuePending}
        documentType={
          printInput.current?.document === PrintableDocument.PACKING_SLIP
            ? "packing slip"
            : "label"
        }
        isBatch={!!isBatch}
        isOpen={showReprintConfirmation}
        isPlural={isPlural ?? false}
        onCancel={() => {
          setShowReprintConfirmation(false);
          setModalData(null);
        }}
        onSuccess={() => {
          datadogLogs.logger.info("Reprint confirmed", {
            userId: user?._id,
            userName: user?.name,
            team: team.name,
            document: printInput.current?.document,
            batch: printInput.current?.batch,
          });
          setShowReprintConfirmation(false);
          if (printInput.current) {
            printBatch(printInput.current);
          }
        }}
        someNeedPrinting={someNeedPrinting}
      />
      {modalData && (
        <PrintBatchModal
          key={getKey(modalData)}
          {...modalData}
          onClose={() => {
            setModalData(null);
            onClose?.();
          }}
          onDocumentPrinted={onDocumentPrinted}
        />
      )}
      {workstationOptions.length && actionPortal
        ? createPortal(
            <Flex>
              <RedoConnectWarning compact />
              {printerStatus}
              <RedoSingleSelectDropdownInput
                className={dropdownClassname}
                fitToAnchor={false}
                options={workstationOptions}
                optionSelected={({ item }) =>
                  setSelectedWorkstation(item.value)
                }
                placeholder="Select workstation"
                selectedItem={selectedWorkstationOption}
                size="xs"
              />
            </Flex>,
            actionPortal,
          )
        : printerOptions.length && actionPortal
          ? createPortal(
              <Flex dir="row" gap="2xl">
                <RedoConnectWarning compact />
                {printerStatus}
                <RedoSingleSelectDropdownInput
                  className={dropdownClassname}
                  fitToAnchor={false}
                  options={printerOptions}
                  optionSelected={({ item }) => setSelectedPrinter(item.value)}
                  placeholder="Select printer"
                  selectedItem={selectedPrinterOption}
                  size="xs"
                />
              </Flex>,
              actionPortal,
            )
          : null}
      {selectedWorkstationObject && (
        <WorkstationSettingsModal
          onClose={() => setWorkstationModalOpen(false)}
          open={workstationModalOpen}
          printProfiles={printProfiles}
          workstation={selectedWorkstationObject}
        />
      )}
    </>
  );
});

const PrintBatchModal = memo(function PrintBatchModal({
  batch,
  document,
  documentPrintStatusMap,
  bulkOneClickPrintPickListPosition,
  templateId,
  workstationId,
  onClose,
  onDocumentPrinted,
}: {
  batch: PrintableBatch;
  document: PrintableDocument;
  documentPrintStatusMap: DocumentPrintStatusMap;
  bulkOneClickPrintPickListPosition?: BulkOneClickPrintPickListPosition;
  templateId?: string;
  workstationId?: string;
  onClose: () => void;
  onDocumentPrinted?: (
    batch: PrintableBatch,
    document: PrintableDocument,
  ) => void;
}) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const [selectedTab, setSelectedTab] = useState<PrintableDocument>(document);
  const [documentUrls, setDocumentUrls] = useState<{
    [key in PrintableDocument]?: string;
  }>({});
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const user = useContext(UserContext);
  const team = useRequiredContext(TeamContext);

  const printLoad = useLoad(
    async (signal) => {
      if (documentUrls[selectedTab]) {
        return { url: documentUrls[selectedTab] };
      }

      try {
        const selectedBulkOneClickPrintPickListPosition =
          selectedTab === PrintableDocument.LABEL
            ? bulkOneClickPrintPickListPosition
            : undefined;
        const result = await doPrintBatch(client, batch, selectedTab, {
          ...getBulkOneClickPrintPickListInput(
            selectedBulkOneClickPrintPickListPosition,
          ),
          templateId,
          workstationId,
          signal,
        });
        if (result.batchFailed) {
          const errorMessage =
            "shipmentErrorMessages" in result &&
            result.shipmentErrorMessages?.length
              ? result.shipmentErrorMessages.join(". ")
              : "Batch failed to generate labels";
          const errorMessages =
            ("shipmentErrorMessages" in result &&
              result.shipmentErrorMessages) ||
            [];
          throw new BatchFailedError(errorMessage, errorMessages);
        }

        setDocumentUrls((prev) => ({ ...prev, [selectedTab]: result.url }));
        return result;
      } catch (error) {
        if (signal.aborted) {
          return null;
        }

        throw error;
      }
    },
    [
      selectedTab,
      batch,
      bulkOneClickPrintPickListPosition,
      templateId,
      workstationId,
    ],
  );

  // Pre-flight check: orders whose packing slip will render a blank weather
  // block because they have no destination address (e.g. local-pickup orders).
  // Best-effort — surfaced as a non-blocking warning so the merchant knows why
  // the weather section is missing instead of re-printing and filing a ticket.
  const weatherWarningLoad = useLoad(
    async (signal) =>
      await client.getPackingSlipWeatherWarnings(
        { ...batch, templateId },
        { signal },
      ),
    [batch, templateId],
  );
  const ordersMissingWeather =
    weatherWarningLoad.value?.ordersMissingWeather ?? [];

  const [printedDocuments, setPrintedDocuments] = useState<
    Partial<Record<PrintableDocument, boolean>>
  >({});

  const selectedTabNeedsPrinting = getNeedsPrintingForDocument(
    documentPrintStatusMap,
    selectedTab,
  );
  const selectedDocumentPrinted = printedDocuments[selectedTab] ?? false;

  const [markPrintedLoad, doMarkPrinted] = useTriggerLoad(async () => {
    const documentToMark = selectedTab;

    try {
      const printedDocumentsToMark = getDocumentsMarkedByPrint(
        documentToMark,
        bulkOneClickPrintPickListPosition,
      );
      for (const printedDocument of printedDocumentsToMark) {
        await client.markAsPrinted({
          groups: batch,
          document: printedDocument,
          workstationId,
          url: documentUrls[printedDocument],
        });
      }
      setPrintedDocuments((prev) => ({
        ...prev,
        ...Object.fromEntries(
          printedDocumentsToMark.map((printedDocument) => [
            printedDocument,
            true,
          ]),
        ),
      }));
      for (const printedDocument of printedDocumentsToMark) {
        onDocumentPrinted?.(batch, printedDocument);
      }
      onClose();
    } catch (error) {
      console.error("Error marking documents as printed", error);
      toast("Failed to mark documents as printed. Please try again", {
        variant: "error",
      });
    }
  });

  const canMarkSelectedDocumentPrinted =
    !("batchId" in batch) ||
    selectedTab === PrintableDocument.LABEL ||
    selectedTab === PrintableDocument.PACKING_SLIP ||
    selectedTab === PrintableDocument.ORDER_SUMMARY;

  const handleCloseRequest = useHandler(() => {
    if (
      canMarkSelectedDocumentPrinted &&
      !selectedDocumentPrinted &&
      !printLoad.pending &&
      !printLoad.error
    ) {
      setShowExitConfirmation(true);
    } else {
      onClose();
    }
  });

  const showPrintButton = !printLoad.pending && canMarkSelectedDocumentPrinted;

  return (
    <>
      <RedoBaseModal isOpen onModalCloseRequested={handleCloseRequest}>
        <RedoModalHeader
          {...getBatchLabelText(batch, document)}
          cancelClicked={handleCloseRequest}
        />
        <Flex data-testid="print-batch-modal" dir="column" gap="none">
          <RedoHorizontalTabs
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            tabs={getDocumentTabs({
              openedDocument: document,
              documentPrintStatusMap,
            })}
          />
          {selectedTab === PrintableDocument.PACKING_SLIP &&
            ordersMissingWeather.length > 0 && (
              <Flex pt="lg" px="2xl">
                <RedoAlert
                  color="warning"
                  description={weatherMissingAddressDescription(
                    ordersMissingWeather,
                  )}
                  fullWidth
                  size="sm"
                  title={weatherMissingAddressTitle(
                    ordersMissingWeather.length,
                  )}
                />
              </Flex>
            )}
          {printLoad.pending ? (
            <Flex align="center" className={styles.iframe} justify="center">
              <CircleSpinner />
              <Text>Generating PDF</Text>
            </Flex>
          ) : printLoad.error ? (
            <Flex
              align="center"
              className={styles.iframe}
              dir="column"
              gap="lg"
              justify="center"
              p="2xl"
            >
              <Text
                fontSize="lg"
                fontWeight="semibold"
                textAlign="center"
                textColor="error"
              >
                Failed to generate {DOCUMENT_PLURAL_LABELS[selectedTab]}
              </Text>
              {printLoad.error instanceof BatchFailedError &&
              printLoad.error.shipmentErrorMessages.length > 0 ? (
                <Flex dir="column" gap="md" overflowY="auto" px="md">
                  {printLoad.error.shipmentErrorMessages.map(
                    (message, index) => (
                      <Text fontSize="sm" key={index} textAlign="center">
                        {message}
                      </Text>
                    ),
                  )}
                </Flex>
              ) : (
                <Text fontSize="sm" textAlign="center">
                  {printLoad.error instanceof ClientError &&
                  printLoad.error.statusCode < 500
                    ? printLoad.error.message
                    : "Failed to generate the requested documents. Please try again or contact support."}
                </Text>
              )}
            </Flex>
          ) : (
            <iframe className={styles.iframe} src={printLoad.value?.url} />
          )}
        </Flex>
        <Flex align="center" dir="row" justify="space-between" p="2xl">
          <Flex p="md">
            {!printLoad.error && (
              <RedoExternalLink
                text="Open PDF in another tab"
                url={printLoad.value?.url}
              />
            )}
          </Flex>
          <Flex dir="row" gap="md">
            <RedoButton
              hierarchy="secondary"
              onClick={handleCloseRequest}
              size="xl"
              text="Close"
            />
            {showPrintButton && !printLoad.error && (
              <RedoButton
                disabled={printLoad.pending || selectedDocumentPrinted}
                hierarchy="primary"
                onClick={() => {
                  datadogLogs.logger.info("Clicked mark as printed", {
                    userId: user?._id,
                    userName: user?.name,
                    teamId: team?._id,
                    document: selectedTab,
                    reprint: !selectedTabNeedsPrinting,
                    batchId: "batchId" in batch ? batch.batchId : undefined,
                    fulfillmentGroupCount:
                      "fulfillmentGroupIds" in batch
                        ? batch.fulfillmentGroupIds.length
                        : undefined,
                    firstFiftyGroupIds:
                      "fulfillmentGroupIds" in batch
                        ? batch.fulfillmentGroupIds.slice(0, 50)
                        : undefined,
                  });
                  doMarkPrinted();
                }}
                pending={markPrintedLoad.pending}
                size="xl"
                text={
                  selectedDocumentPrinted
                    ? "Document printed"
                    : selectedTabNeedsPrinting
                      ? "Mark printed"
                      : "Mark reprinted"
                }
              />
            )}
          </Flex>
        </Flex>
      </RedoBaseModal>
      <ExitWithoutPrintingConfirmationModal
        isOpen={showExitConfirmation}
        onCancel={() => setShowExitConfirmation(false)}
        onConfirm={() => {
          datadogLogs.logger.info(
            "Exiting print batch modal without marking as printed",
            {
              userId: user?._id,
              userName: user?.name,
              teamId: team?._id,
              document: selectedTab,
              batchId: "batchId" in batch ? batch.batchId : undefined,
              fulfillmentGroupCount:
                "fulfillmentGroupIds" in batch
                  ? batch.fulfillmentGroupIds.length
                  : undefined,
              firstFiftyGroupIds:
                "fulfillmentGroupIds" in batch
                  ? batch.fulfillmentGroupIds.slice(0, 50)
                  : undefined,
            },
          );
          setShowExitConfirmation(false);
          onClose();
        }}
      />
    </>
  );
});

export const PrintToastAction = memo(function PrintToastAction({
  print,
  snackbarId,
}: {
  print: () => Promise<boolean>;
  snackbarId: SnackbarKey;
}) {
  const [load, doPrint] = useTriggerLoad(async () => print());
  // FIXME
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(doPrint, []);

  useLoad(async () => {
    if (load.value) {
      await sleep(Temporal.Duration.from({ seconds: 3 }));
      closeSnackbar(snackbarId);
    }
  }, [load.value]);

  useEffect(() => {
    if (load.error) {
      console.error("Error printing document", load.error);
    }
  }, [load.error]);

  if (load.pending) {
    return (
      <Flex align="center" justify="center">
        <CircleSpinner />
        <Text fontSize="xs" fontWeight="thin">
          Generating PDF
        </Text>
      </Flex>
    );
  }

  if (load.error) {
    return (
      <Flex align="center" justify="center">
        <Text fontSize="xs" fontWeight="thin" textColor="error">
          Something went wrong
        </Text>
        <RedoButton onClick={() => closeSnackbar(snackbarId)} text="Dismiss" />
        <RedoButton onClick={() => doPrint()} text="Retry" />
      </Flex>
    );
  }

  if (load.value) {
    return (
      <Flex align="center" justify="center">
        <Text fontSize="xs" fontWeight="thin" textColor="success">
          Printed
        </Text>
      </Flex>
    );
  }

  return null;
});

/**
 * Displays the result of adding a document to the print queue.
 * Because the print queue is handled in the browser, this should
 * be fast enough that we don't need to show a pending state.
 */
const AddToPrintQueueToastAction = memo(function AddToPrintQueueToastAction({
  addToPrintQueue,
  snackbarId,
}: {
  addToPrintQueue: () => Promise<boolean>;
  snackbarId: SnackbarKey;
}) {
  const [load, doAddToPrintQueue] = useTriggerLoad(async () =>
    addToPrintQueue(),
  );
  // FIXME
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(doAddToPrintQueue, []);

  useLoad(async () => {
    if (load.value) {
      await sleep(Temporal.Duration.from({ seconds: 3 }));
      closeSnackbar(snackbarId);
    }
  }, [load.value]);

  useEffect(() => {
    if (load.error) {
      console.error("Error adding job to print queue", load.error);
    }
  }, [load.error]);

  if (load.error) {
    return (
      <Flex align="center" justify="center">
        <Text fontSize="xs" fontWeight="thin" textColor="error">
          Something went wrong
        </Text>
        <RedoButton onClick={() => closeSnackbar(snackbarId)} text="Dismiss" />
        <RedoButton onClick={() => doAddToPrintQueue()} text="Retry" />
      </Flex>
    );
  }

  if (load.value) {
    return (
      <Flex align="center" justify="center">
        <Text fontSize="xs" fontWeight="thin" textColor="success">
          Success
        </Text>
      </Flex>
    );
  }

  return null;
});

export const printerIdStore = storeTransform(
  localStore("redo.selectedPrinter"),
  bijectionOptional(numberStringFormat),
);

export const workstationIdStore = storeTransform(
  localStore("redo.selectedWorkstation"),
  { read: (value) => value ?? "", write: identity },
);

export function useWorkstationOptions(
  workstations: readonly Pick<Workstation, "_id" | "name">[] | undefined,
): TextRedoListItem<string>[] {
  return useMemo(() => {
    const options: TextRedoListItem<string>[] =
      workstations
        ?.filter((workstation) => !!workstation._id)
        .map((workstation) => ({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          id: workstation._id!,
          type: "text",
          text: workstation.name,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          value: workstation._id!,
        })) || [];
    if (workstations?.length) {
      options.push({
        id: "",
        value: "",
        type: "text",
        text: "No workstation selected",
      });
    }
    return options;
  }, [workstations]);
}

type PrintQueueContextValue = ContextType<typeof PrintQueueContext>;

function getCanQueuePackingSlipPrintJob(
  workstation: Workstation,
): { printOptions: PrintOptions } | undefined {
  const packingSlipPrinter = workstation.selectedPrinters.find(
    (p) => p.format === PageFormat.Letter,
  );

  if (!packingSlipPrinter?.printerId) {
    return undefined;
  }

  const printOptions: PrintOptions = {
    printerId: packingSlipPrinter.printerId,
    workstationId: workstation._id,
  };
  printOptions.printerOptions = packingSlipPrinter.printerOptions;

  return { printOptions };
}

interface HandleWorkstationPrintingInput {
  batch: PrintableBatch;
  document: PrintableDocument;
  packingSlips: PackingSlip[];
  personalizedSlips: PersonalizedSlip[];
  printProfiles: readonly PrintProfile[];
  printQueue: PrintQueueContextValue;
  team: TeamWith<{ outboundLabels: OutboundLabelSettings }>;
  workstation: Workstation;
}

function handleWorkstationPrinting({
  batch,
  document,
  packingSlips,
  personalizedSlips,
  printProfiles,
  printQueue,
  team,
  workstation,
}: HandleWorkstationPrintingInput): PrintOptions {
  const defaultPrintSettings = resolveDefaultPrintSettings(
    team.settings.outboundLabels.documents,
  );
  const defaultPrintPersonalizedSlipOnLabelPrinted =
    team.settings.outboundLabels.scanVerify
      ?.personalizedSlipAutoPrintTrigger ===
    PersonalizedSlipAutoPrintTrigger.LABEL_PRINTED;
  let defaultWorkstationPrintSettings = defaultPrintSettings;
  if (defaultPrintSettings.shippingLabel) {
    defaultWorkstationPrintSettings = {
      ...defaultPrintSettings,
      shippingLabel: {
        ...defaultPrintSettings.shippingLabel,
        printPersonalizedSlipOnLabelPrinted:
          defaultPrintPersonalizedSlipOnLabelPrinted,
      },
    };
  }

  const printSettings = getPrintSettings(
    defaultWorkstationPrintSettings,
    printProfiles,
    team.settings.outboundLabels.workstations,
    workstation._id,
  );
  const printOptions: PrintOptions = { workstationId: workstation._id };
  const pageFormat = getDocumentPageFormat(
    printSettings,
    document,
    packingSlips,
    personalizedSlips,
  );

  const packingSlipPageFormat = getDocumentPageFormat(
    printSettings,
    PrintableDocument.PACKING_SLIP,
    packingSlips,
    personalizedSlips,
  );
  const packingSlipOnLabelPrintedDecisionInput = {
    document,
    labelPageFormat: pageFormat,
    packingSlipPageFormat,
    printSlipOnLabelPrinted:
      printSettings.shippingLabel?.printSlipOnLabelPrinted,
    packingSlips,
  };
  const canQueuePackingSlipPrintJob =
    getCanQueuePackingSlipPrintJob(workstation);
  const shouldQueuePackingSlipWithLabel =
    getShouldPrintPackingSlipOnLabelPrinted(
      packingSlipOnLabelPrintedDecisionInput,
    ) && !!canQueuePackingSlipPrintJob;
  // We need to type narrow canQueuePackingSlipPrintJob to make
  // TypeScript happy here.
  const packingSlipPrintJob = shouldQueuePackingSlipWithLabel
    ? canQueuePackingSlipPrintJob
    : undefined;

  if (packingSlipPrintJob) {
    console.error(`Printing packing slip for ${team.name} ${team._id}`);
    try {
      const documentTitle = getPluralAwareDocumentTitle(
        batch,
        PrintableDocument.PACKING_SLIP,
      );
      const addSlipToPrintQueue = async () => {
        if (!printQueue) {
          throw new Error("Print queue not found");
        }
        printQueue.addJob({
          ...batch,
          ...packingSlipPrintJob.printOptions,
          documentType: PrintableDocument.PACKING_SLIP,
          trigger: PrintJobTrigger.Autolabelprinted,
          workstationId: workstation._id,
        });
        return true;
      };
      enqueueSnackbar(
        <Text fontSize="sm">Adding {documentTitle} to print queue</Text>,
        {
          variant: "default",
          persist: true,
          action: (id) => (
            <AddToPrintQueueToastAction
              addToPrintQueue={addSlipToPrintQueue}
              snackbarId={id}
            />
          ),
        },
      );
    } catch (error) {
      console.error(
        "Error while printing packing slip on label printed",
        error,
      );
    }
  }

  // Auto-print personalized insert when labels are printed
  if (
    document === PrintableDocument.LABEL &&
    printSettings.shippingLabel?.printPersonalizedSlipOnLabelPrinted &&
    personalizedSlips.length > 0
  ) {
    try {
      const slipPrinter = getPersonalizedSlipPrinterForLabelPrint(workstation);

      const slipPrintOptions: PrintOptions = { workstationId: workstation._id };
      slipPrintOptions.printerId = slipPrinter?.printerId;
      slipPrintOptions.printerOptions = slipPrinter?.printerOptions;

      if (slipPrintOptions.printerId) {
        const documentTitle = getPluralAwareDocumentTitle(
          batch,
          PrintableDocument.PERSONALIZED_SLIP,
        );
        const addSlipToPrintQueue = async () => {
          if (!printQueue) {
            throw new Error("Print queue not found");
          }
          printQueue.addJob({
            ...batch,
            ...slipPrintOptions,
            documentType: PrintableDocument.PERSONALIZED_SLIP,
            trigger: PrintJobTrigger.Autolabelprinted,
            workstationId: workstation._id,
          });
          return true;
        };
        enqueueSnackbar(
          <Text fontSize="sm">Adding {documentTitle} to print queue</Text>,
          {
            variant: "default",
            persist: true,
            action: (id) => (
              <AddToPrintQueueToastAction
                addToPrintQueue={addSlipToPrintQueue}
                snackbarId={id}
              />
            ),
          },
        );
      }
    } catch (error) {
      console.error(
        "Error while printing personalized insert on label printed",
        error,
      );
    }
  }

  const selectedPrinter = getSelectedPrinter(workstation, document, pageFormat);

  printOptions.printerId = selectedPrinter?.printerId;
  printOptions.printerOptions = selectedPrinter?.printerOptions;

  return printOptions;
}

function getBatchLabelText(batch: PrintableBatch, document: PrintableDocument) {
  const subtitle =
    "batchNumber" in batch
      ? `Batch #${batch.batchNumber}`
      : batch.fulfillmentGroupIds.length > 1
        ? `${batch.fulfillmentGroupIds.length} orders`
        : "1 order";

  let title: string;
  switch (document) {
    case PrintableDocument.LABEL:
      title = "Print shipping labels";
      break;
    case PrintableDocument.RETURN_LABEL:
      title = "Print return labels";
      break;
    case PrintableDocument.PACKING_SLIP:
      title = "Print packing slips";
      break;
    case PrintableDocument.COMMERCIAL_INVOICE:
      title = "Print commercial invoices";
      break;
    case PrintableDocument.PERSONALIZED_SLIP:
      title = "Print personalized inserts";
      break;
    case PrintableDocument.PICK_LIST:
      title = "Print pick list";
      break;
    case PrintableDocument.ORDER_SUMMARY:
      title = "Print order summary";
      break;
    case PrintableDocument.LOCAL_PICK_UP_LABEL:
      title = "Print local pickup labels";
      break;
    case PrintableDocument.BILL_OF_LADING:
      title = "Print bill of lading";
      break;
    case PrintableDocument.PARCEL_PACKING_SLIP:
      title = "Print package packing slips";
      break;
    default:
      assertNever(document);
  }

  return { subtitle, title };
}

function getPluralAwareDocumentTitle(
  batch: PrintableBatch,
  document: PrintableDocument,
) {
  const plural =
    ("fulfillmentGroupIds" in batch && batch.fulfillmentGroupIds.length > 1) ||
    ("batchId" in batch && batch.batchId);
  const documentDisplayName = PrintableDocumentsDisplayName[document];
  return plural ? `${documentDisplayName}s` : documentDisplayName;
}

function getDocumentPageFormat(
  printSettings: PrintSettings,
  document: PrintableDocument,
  packingSlips: PackingSlip[],
  personalizedSlips: PersonalizedSlip[],
) {
  switch (document) {
    case PrintableDocument.LABEL:
      return printSettings.shippingLabel?.format ?? PageFormat.Label;
    case PrintableDocument.PACKING_SLIP:
      if (packingSlips.length === 0) {
        return PageFormat.Label; // Consistent with defaultTemplate
      }

      if (
        packingSlips.every(
          (t) =>
            t.pageLayout.size === PageSize.FULL_SIZE ||
            t.pageLayout.size === PageSize.HALF_SIZE ||
            t.pageLayout.size === PageSize.CARD_5X7,
        )
      ) {
        return PageFormat.Letter;
      }
      // Consistent with defaultTemplate
      return PageFormat.Label;
    case PrintableDocument.PICK_LIST:
      return printSettings.pickList?.format ?? PageFormat.Letter;
    case PrintableDocument.ORDER_SUMMARY:
      return PageFormat.Letter;
    case PrintableDocument.COMMERCIAL_INVOICE:
      return PageFormat.Letter;
    case PrintableDocument.PERSONALIZED_SLIP:
      if (personalizedSlips.length === 0) {
        return PageFormat.Label;
      }

      if (
        personalizedSlips.every(
          (t) =>
            t.globalSettings.pageLayout.size === PageSize.FULL_SIZE ||
            t.globalSettings.pageLayout.size === PageSize.HALF_SIZE ||
            t.globalSettings.pageLayout.size === PageSize.CARD_5X7,
        )
      ) {
        return PageFormat.Letter;
      }
      if (
        personalizedSlips.every(
          (t) => t.globalSettings.pageLayout.size === PageSize.LABEL,
        )
      ) {
        return PageFormat.Label;
      }
      // Consistent with defaultTemplate
      return PageFormat.Label;
    case PrintableDocument.RETURN_LABEL:
      // Use same format as regular labels for printer compatibility
      return printSettings.shippingLabel?.format ?? PageFormat.Label;
    case PrintableDocument.LOCAL_PICK_UP_LABEL:
      return PageFormat.Label;
    case PrintableDocument.BILL_OF_LADING:
      return PageFormat.Letter;
    case PrintableDocument.PARCEL_PACKING_SLIP:
      if (packingSlips.length === 0) {
        return PageFormat.Label;
      }
      if (
        packingSlips.every(
          (t) =>
            t.pageLayout.size === PageSize.FULL_SIZE ||
            t.pageLayout.size === PageSize.HALF_SIZE ||
            t.pageLayout.size === PageSize.CARD_5X7,
        )
      ) {
        return PageFormat.Letter;
      }
      return PageFormat.Label;
    default:
      assertNever(document);
  }
}

function getSelectedPrinter(
  workstation: Workstation,
  document: PrintableDocument,
  pageFormat: PageFormat,
) {
  if (document === PrintableDocument.PERSONALIZED_SLIP) {
    return getPersonalizedSlipPrinterForLabelPrint(workstation);
  }
  return workstation.selectedPrinters.find(
    (p) => p.format === pageFormat && !p.isPremium,
  );
}

const ExitWithoutPrintingConfirmationModal = memo(
  function ExitWithoutPrintingConfirmationModal({
    isOpen,
    onCancel,
    onConfirm,
  }: {
    isOpen: boolean;
    onCancel: () => void;
    onConfirm: () => void;
  }) {
    return (
      <RedoBaseModal
        isOpen={isOpen}
        onModalCloseRequested={onCancel}
        otherClasses={[styles.exitConfirmationModal]}
      >
        <RedoModalHeader
          cancelClicked={onCancel}
          subtitle="You haven't marked this document as printed yet"
          title="Are you sure you want to exit?"
        />
        <Flex dir="column" gap="2xl" p="2xl" pt="none">
          <Text fontSize="md" textColor="primary">
            If you exit without marking as printed, you may lose track of which
            documents have been printed, and fulfillments may still merge.
          </Text>
          <Flex dir="row" gap="md" justify="flex-end">
            <RedoButton
              hierarchy="secondary"
              onClick={onCancel}
              size="xl"
              text="Go back"
            />
            <RedoButton
              hierarchy="primary"
              onClick={onConfirm}
              size="xl"
              text="Exit anyway"
            />
          </Flex>
        </Flex>
      </RedoBaseModal>
    );
  },
);
